/**
 * `window.theme.variantUtils` — stateless variant-matching helpers shared
 * by the PDP variant picker (via `Alpine.store('pdp')`) and the Quick View
 * modal (via `Alpine.data('quickAddForm')`).
 *
 * Functions are pure: pass the product payload (as returned by
 * `/products/{handle}.js`) and a selection / id / values array, get an
 * answer back. No DOM access, no Alpine dependency, no globals other
 * than the export.
 */
(function () {
  var utils = {
    /**
     * Find a variant by ordered option values.
     * @param {Object} payload - Shopify product JSON payload (with `variants` array).
     * @param {Array<string>} values - Ordered option values matching the payload's option positions.
     * @returns {Object|null} The matching variant object, or null when no match / no payload.
     */
    resolveVariantFromValues: function (payload, values) {
      if (!payload || !Array.isArray(payload.variants) || !Array.isArray(values)) return null;
      for (var i = 0; i < payload.variants.length; i++) {
        var v = payload.variants[i];
        /* `variant.options` is the canonical array, but some snapshots in
           the wild carry only the flattened `option1/2/3` — support both
           so callers don't have to normalise before calling. */
        var optionsArr = Array.isArray(v.options)
          ? v.options
          : [v.option1, v.option2, v.option3].filter(function (o) { return o != null; });
        if (optionsArr.length !== values.length) continue;
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (String(optionsArr[j]) !== String(values[j])) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    },

    /**
     * Given a partial `{ [1-based position]: value }` selection, return
     * true when at least one variant matches every supplied position.
     * Positions not present in the selection act as wildcards. Used by
     * the picker to grey out non-viable option values without waiting
     * for the shopper to reach an invalid combination.
     *
     * Returns `true` when the payload hasn't loaded yet — callers keep
     * every input enabled until the JSON arrives (optimistic default).
     *
     * @param {Object|null} payload - Product JSON payload (or null).
     * @param {Object} selection - `{ '1': 'Red', '2': 'Small' }` shape.
     * @returns {boolean}
     */
    hasVariantForPartial: function (payload, selection) {
      if (!payload || !Array.isArray(payload.variants)) return true;
      var positions = Object.keys(selection || {});
      if (positions.length === 0) return true;
      for (var i = 0; i < payload.variants.length; i++) {
        var v = payload.variants[i];
        var optionsArr = Array.isArray(v.options)
          ? v.options
          : [v.option1, v.option2, v.option3].filter(function (o) { return o != null; });
        var ok = true;
        for (var p = 0; p < positions.length; p++) {
          var position = positions[p];
          if (optionsArr[Number(position) - 1] !== selection[position]) { ok = false; break; }
        }
        if (ok) return true;
      }
      return false;
    },

    /**
     * Return a payload whose `variants` array excludes the given ids.
     * Used to drop variants hidden from the Online Store channel (B2B
     * catalog contexts serve every variant regardless of per-variant
     * channel publication) so no client-side resolution can re-select
     * them.
     *
     * The payload's aggregate price fields (`price_min`, `price_max`,
     * `price_varies`, `compare_at_price_min`, `compare_at_price_max`) are
     * ALSO recomputed from the surviving variants, because Shopify computes
     * them across ALL served variants — including the pos-only ones this
     * filter drops — so a range getter reading them would otherwise still
     * advertise a hidden variant's ceiling. `compare_at_price` falls back to
     * the variant's own `price` when null, mirroring the store convention
     * that an undiscounted variant carries `compare_at_price == price`, so
     * the compare range can never floor to 0.
     *
     * FAIL-OPEN: when `excludeIds` is empty / absent, or the payload has
     * no variants array, the original payload is returned UNCHANGED (same
     * reference) — a no-op with zero allocation, which is the case for any
     * all-online product (every variant published to the Online Store). The
     * recompute therefore runs ONLY when there is at least one variant to
     * drop.
     *
     * @param {Object|null} payload - Shopify product JSON payload.
     * @param {Set<string>|Array<string>|null} excludeIds - Variant ids to drop.
     * @returns {Object|null} Payload with hidden variants removed and price aggregates recomputed, or the input unchanged.
     */
    filterHiddenVariants: function (payload, excludeIds) {
      if (!payload || !Array.isArray(payload.variants)) return payload;
      var exclude = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);
      if (exclude.size === 0) return payload;

      var visibleVariants = payload.variants.filter(function (v) {
        return !exclude.has(String(v.id));
      });
      var filtered = Object.assign({}, payload, { variants: visibleVariants });

      /* Recompute the aggregate price range over the visible variants only.
         Skip when every variant was dropped — leave the native aggregates
         rather than emit an empty range. */
      if (visibleVariants.length > 0) {
        var priceMin = null;
        var priceMax = null;
        var compareMin = null;
        var compareMax = null;
        visibleVariants.forEach(function (v) {
          var price = Number(v.price);
          if (priceMin === null || price < priceMin) priceMin = price;
          if (priceMax === null || price > priceMax) priceMax = price;
          var compare = v.compare_at_price == null ? Number(v.price) : Number(v.compare_at_price);
          if (compareMin === null || compare < compareMin) compareMin = compare;
          if (compareMax === null || compare > compareMax) compareMax = compare;
        });
        filtered.price_min = priceMin;
        filtered.price_max = priceMax;
        filtered.price_varies = priceMin !== priceMax;
        filtered.compare_at_price_min = compareMin;
        filtered.compare_at_price_max = compareMax;
      }

      return filtered;
    },
  };

  window.theme = window.theme || {};
  window.theme.variantUtils = utils;
})();
